/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { Image, View } from "react-native";

import { Text, TextInput } from "UpduoLibrary";
import {
  upduoSecondLightestGray,
  skaleDeactiveGray,
  skalePrimaryBlue,
  upduoKudosBlue,
  upduoKudosCyan,
  upduoKudosOrange,
  upduoKudosPurple,
} from "config/UpduoColors";
import KudosChoiceButton from "./KudosChoiceButton";
import friendly from "assets/kudos_icons/friendly.png";
import goodConversation from "assets/kudos_icons/good_conversation.png";
import helpfulTips from "assets/kudos_icons/helpful_tips.png";
import knowledgeable from "assets/kudos_icons/knowledgeable.png";

const images = {
  friendly: friendly,
  good_conversation: goodConversation,
  helpful_tips: helpfulTips,
  knowledgeable: knowledgeable,
};

interface KudosModalProps {
  partnerFirstName: string;
  kudosSelectedValue: object;
  onKudosSelectedValueChange: (element: any) => void;
  onKudosMessageChange: (message: string) => void;
}

export default function KudosModal({
  partnerFirstName,
  kudosSelectedValue,
  onKudosSelectedValueChange,
  onKudosMessageChange,
}: KudosModalProps) {
  // Use local state to prevent textbox jitter
  const [localKudosMessage, setLocalKudosMessage] = useState("");

  const kudosOptions = [
    {
      iconContent: (
        <Image
          source={images["friendly"]}
          style={{ width: 20, height: 20, resizeMode: "contain" }}
        />
      ),
      title: "Friendly",
      color: upduoKudosPurple,
    },
    {
      iconContent: (
        <Image
          source={images["knowledgeable"]}
          style={{ width: 20, height: 20, resizeMode: "contain" }}
        />
      ),
      title: "Knowledgeable",
      color: upduoKudosBlue,
    },
    {
      iconContent: (
        <Image
          source={images["good_conversation"]}
          style={{ width: 20, height: 20, resizeMode: "contain" }}
        />
      ),
      title: "Good conversation",
      color: upduoKudosOrange,
    },
    {
      iconContent: (
        <Image
          source={images["helpful_tips"]}
          style={{ width: 20, height: 20, resizeMode: "contain" }}
        />
      ),
      title: "Helpful tips",
      color: upduoKudosCyan,
    },
  ];

  return (
    <View
      // We need this parent component because the card must fill the remaining space,
      // but we want contentHeight to be defined by the contents alone
      style={{ flex: 1, paddingHorizontal: 30 }}
    >
      <View style={{ paddingTop: 30 }}>
        <Text isHeading={true} style={{ fontSize: 16, marginBottom: 20 }}>
          {`Send ${partnerFirstName} Kudos!`}
        </Text>
        <View
          style={{ flexWrap: "wrap", display: "flex", flexDirection: "row" }}
        >
          {kudosOptions.map((kudosOption, index) => {
            return (
              <KudosChoiceButton
                key={index}
                title={kudosOption.title}
                iconContent={kudosOption.iconContent}
                color={kudosOption.color}
                isSelected={kudosSelectedValue[`${kudosOption.title}`]}
                _handleKudosChoicePress={() => {
                  onKudosSelectedValueChange(kudosOption.title);
                }}
              />
            );
          })}
        </View>
        {Object.values(kudosSelectedValue).some((element) => element) ? (
          <View>
            <Text isHeading={true} style={{ fontSize: 16, marginBottom: 20 }}>
              {"Leave them a message!"}
            </Text>
            <TextInput
              style={{
                textAlignVertical: "top",
                borderColor: upduoSecondLightestGray,
                height: 100,
                paddingTop: 20,
                paddingBottom: 20,
                paddingHorizontal: 20,
                borderWidth: 1,
                borderRadius: 8,
              }}
              selectionColor={skalePrimaryBlue}
              placeholderTextColor={skaleDeactiveGray}
              multiline={true}
              onChangeText={(text) => {
                onKudosMessageChange(text);
                setLocalKudosMessage(text);
              }}
              value={localKudosMessage}
              underlineColorAndroid={"transparent"}
              placeholder={"Enter your message"}
            />
          </View>
        ) : null}
      </View>
    </View>
  );
}
